import * as React from "react";
import { cn } from "@/lib/utils";
import { CTA_LIBRARY, type CtaIntent } from "@/lib/ctaLibrary";

/**
 * CtaGroup — groups CTAs so only one primary action lives per group and
 * renders the style-guide microcopy line beneath.
 */
export interface CtaGroupProps extends React.HTMLAttributes<HTMLDivElement> {
  /** Intent whose microcopy is displayed under the group. */
  microcopyFor?: CtaIntent;
  /** Override the microcopy text. Prefer the library default. */
  microcopy?: React.ReactNode;
  align?: "start" | "center" | "end";
}

const ALIGN: Record<NonNullable<CtaGroupProps["align"]>, string> = {
  start: "sm:justify-start",
  center: "sm:justify-center",
  end: "sm:justify-end",
};

export function CtaGroup({
  children,
  microcopyFor,
  microcopy,
  align = "center",
  className,
  ...rest
}: CtaGroupProps) {
  const line =
    microcopy ??
    (microcopyFor ? CTA_LIBRARY[microcopyFor].microcopy : undefined);

  // Dev-only guard: warn if multiple primary CTAs render in the same group.
  if (import.meta.env?.DEV) {
    let primaries = 0;
    React.Children.forEach(children, (child) => {
      if (!React.isValidElement(child)) return;
      const props = child.props as { hierarchy?: string; intent?: CtaIntent };
      const hierarchy =
        props.hierarchy ??
        (props.intent ? CTA_LIBRARY[props.intent].defaultHierarchy : undefined);
      if (hierarchy === "primary") primaries += 1;
    });
    if (primaries > 1) {
      console.warn(
        "[CtaGroup] More than one primary CTA detected. Demote extras to hierarchy=\"secondary\".",
      );
    }
  }

  return (
    <div className={cn("space-y-2", className)} {...rest}>
      <div
        className={cn(
          "flex flex-col sm:flex-row gap-3 justify-center",
          ALIGN[align],
        )}
      >
        {children}
      </div>
      {line ? (
        <p className="text-[11px] text-muted-foreground text-center">
          {line}
        </p>
      ) : null}
    </div>
  );
}
